import type { z } from 'zod';
import type { schemas } from '@shared/index';

type Category = z.infer<typeof schemas.comms.notification>['category'];

/** Category keys are identifiers; these are the words a person reads. */
export const CATEGORY_LABEL: Record<Category, string> = {
  leave: 'Leave',
  attendance: 'Attendance',
  tasks: 'Tasks',
  workReports: 'Work reports',
  approvals: 'Approvals',
  announcements: 'Announcements',
  onboarding: 'Onboarding',
  documents: 'Documents',
  security: 'Security',
  system: 'System',
};

/**
 * What each category covers, shown beside its toggle.
 *
 * A preferences screen that lists nine bare category names makes people guess what they
 * are switching off, and the usual guess is "all of it".
 */
export const CATEGORY_DESCRIPTION: Record<Category, string> = {
  leave: 'Your requests, and decisions on them.',
  attendance: 'Punch reminders, missing punch-outs and corrections.',
  tasks: 'Assignments, deadlines and completions.',
  workReports: 'Reports submitted to you, and reviews of yours.',
  approvals: 'Requests waiting on your decision.',
  announcements: 'Company-wide posts and holidays.',
  onboarding: 'Invitations, probation and onboarding steps.',
  documents: 'Expiring documents and verification outcomes.',
  security: 'Password changes and new sign-ins. Always delivered.',
  system: 'Failures an administrator needs to know about.',
};

/** Categories a user may not silence. */
export const ALWAYS_ON: Category[] = ['security'];

type EventKey = z.infer<typeof schemas.comms.notification>['eventKey'];

/**
 * Event keys are identifiers; these are the words an administrator reads in the policy
 * matrix and the delivery log. `Record`, not a partial map, so adding an event to the
 * catalog without labelling it is a compile error rather than a raw key on screen.
 */
export const EVENT_LABEL: Record<EventKey, string> = {
  'auth.passwordChanged': 'Password changed',
  'auth.newDeviceLogin': 'Sign-in from a new device',
  'employee.invited': 'Employee invited',
  'employee.activated': 'Employee activated their account',
  'employee.probationDue': 'Probation review due',
  'attendance.punchReminder': 'Punch-in reminder',
  'attendance.missingPunchOut': 'Missing punch-out',
  'attendance.correctionSubmitted': 'Attendance correction requested',
  'attendance.correctionDecided': 'Attendance correction decided',
  'leave.submitted': 'Leave requested',
  'leave.approved': 'Leave approved',
  'leave.rejected': 'Leave rejected',
  'leave.cancelled': 'Leave cancelled',
  'leave.balanceAdjusted': 'Leave balance adjusted',
  'approval.pending': 'Approval waiting for you',
  'approval.reassigned': 'Approval reassigned',
  'approval.overridden': 'Approval overridden',
  'task.assigned': 'Task assigned',
  'task.reassigned': 'Task reassigned',
  'task.deadlineApproaching': 'Task deadline approaching',
  'task.overdue': 'Task overdue',
  'task.completed': 'Task completed',
  'workReport.submitted': 'Work report submitted',
  'workReport.reviewed': 'Work report reviewed',
  'workReport.changesRequested': 'Work report needs changes',
  'workReport.reminder': 'Work report reminder',
  'announcement.published': 'Announcement published',
  'document.expiringSoon': 'Document expiring soon',
  'document.verified': 'Document verified',
  'document.rejected': 'Document rejected',
  'onboarding.taskAssigned': 'Onboarding task assigned',
  'holiday.published': 'Holiday added',
  'workSession.interrupted': 'Work session interrupted',
  'workSession.staleTruncated': 'Work session truncated',
  'system.jobFailed': 'Scheduled job failed',
};
